import React, { useState, useRef, useEffect } from 'react';
import { toast } from 'sonner';
import { BobineSceau, SceauButton, SceauIndicator } from '../BobineSceau';
import { playGoldSound } from '../../hooks/useGoldSound';

const API = `${(process.env.REACT_APP_BACKEND_URL || 'https://bobine.live')}/api`;

/* Dropdown "Ranger dans un écrin" — position: absolute, soudé au cadre de la photo */
const AlbumPickerDropdown = ({ photo, cardUid, albums, setAlbums, albumPickerPhotoId, setAlbumPickerPhotoId, setDuplicateEcrin, token }) => {
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  if (albumPickerPhotoId !== cardUid) return null;

  const handleCreate = async (e) => {
    e.stopPropagation();
    const name = newName.trim();
    if (!name) return;
    try {
      const res = await fetch(`${API}/albums/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ name }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || 'Création impossible');
      const newAlbum = { ...data.album, photo_ids: [photo.id], photo_count: 1 };
      // Add the photo into the freshly created album
      await fetch(`${API}/albums/${newAlbum.id}/add-photos`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ photo_ids: [photo.id] }),
      });
      setAlbums((prev) => [...prev, newAlbum]);
      toast(`Écrin « ${name} » créé · trésor ajouté`, { style: { fontFamily: "'Georgia', serif", backgroundColor: '#0a0a0a', border: '1px solid rgba(212,175,55,0.3)', color: '#D4AF37' }, duration: 2500 });
      setNewName('');
      setCreating(false);
      setAlbumPickerPhotoId(null);
    } catch (err) {
      toast.error(err.message || 'Création impossible');
    }
  };

  return (
    <>
      <div
        onClick={(e) => { e.stopPropagation(); setAlbumPickerPhotoId(null); setCreating(false); setNewName(''); }}
        style={{ position: 'absolute', inset: 0, zIndex: 18, backgroundColor: 'rgba(0,0,0,0.5)', borderRadius: 10 }}
      />
      <div data-testid={`album-picker-${photo.id}`} onClick={e => e.stopPropagation()} style={{
        position: 'absolute',
        bottom: 50, left: 4, right: 4,
        zIndex: 19,
        backgroundColor: '#0a0a0a', border: '1px solid rgba(212,175,55,0.25)', borderRadius: 8,
        boxShadow: '0 -4px 20px rgba(0,0,0,0.6)', maxHeight: 280, overflowY: 'auto',
      }}>
        <div style={{ padding: '8px 12px', borderBottom: '1px solid rgba(212,175,55,0.08)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontFamily: "'Georgia', serif", fontSize: 11, color: '#D4AF37' }}>Gérer les écrins</span>
          <button data-testid="close-album-picker" onClick={(e) => { e.stopPropagation(); setAlbumPickerPhotoId(null); setCreating(false); setNewName(''); }}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px 4px', color: '#FAF3E0', fontSize: 14, opacity: 0.6, lineHeight: 1 }}
            onMouseEnter={e => e.currentTarget.style.opacity = '1'}
            onMouseLeave={e => e.currentTarget.style.opacity = '0.6'}>
            &#x2715;
          </button>
        </div>
        {albums.map(a => {
          const alreadyIn = (a.photo_ids || []).includes(photo.id);
          return (
            <button key={a.id} data-testid={`picker-album-${a.id}`}
              onClick={async (e) => {
                e.stopPropagation();
                try {
                  if (alreadyIn) {
                    await fetch(`${API}/albums/${a.id}/remove-photos`, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify({ photo_ids: [photo.id] }) });
                    setAlbums(prev => prev.map(al => al.id === a.id ? { ...al, photo_count: Math.max(0, (al.photo_count || 1) - 1), photo_ids: (al.photo_ids || []).filter(pid => pid !== photo.id) } : al));
                    toast(`Trésor retiré de ${a.name}`, { style: { fontFamily: "'Georgia', serif", backgroundColor: '#0a0a0a', border: '1px solid rgba(212,175,55,0.3)', color: '#D4AF37' }, duration: 2500 });
                  } else {
                    await fetch(`${API}/albums/${a.id}/add-photos`, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify({ photo_ids: [photo.id] }) });
                    setAlbums(prev => prev.map(al => al.id === a.id ? { ...al, photo_count: (al.photo_count || 0) + 1, photo_ids: [...(al.photo_ids || []), photo.id] } : al));
                    toast(`Trésor ajouté à ${a.name}`, { style: { fontFamily: "'Georgia', serif", backgroundColor: '#0a0a0a', border: '1px solid rgba(212,175,55,0.3)', color: '#D4AF37' }, duration: 2500 });
                  }
                } catch { /* silent */ }
                setAlbumPickerPhotoId(null);
              }}
              style={{ width: '100%', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 8, fontFamily: "'Georgia', serif", fontSize: 12, color: '#D4AF37', background: 'none', border: 'none', padding: '9px 12px', cursor: 'pointer', transition: 'all 0.2s', opacity: alreadyIn ? 1 : 0.7 }}
              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(212,175,55,0.06)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'none'; }}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z"/></svg>
              {a.name}
              <span style={{ fontSize: 9, marginLeft: 'auto', fontWeight: 600 }}>{alreadyIn ? 'Retirer' : 'Ajouter'}</span>
            </button>
          );
        })}

        {/* "+ Nouvel Écrin" — création inline */}
        <div style={{ borderTop: '1px solid rgba(212,175,55,0.08)' }}>
          {!creating ? (
            <button data-testid={`picker-create-${photo.id}`}
              onClick={(e) => { e.stopPropagation(); setCreating(true); }}
              style={{ width: '100%', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 8, fontFamily: "'Georgia', serif", fontSize: 12, color: '#D4AF37', background: 'none', border: 'none', padding: '9px 12px', cursor: 'pointer', transition: 'all 0.2s', fontStyle: 'italic' }}
              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(212,175,55,0.06)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'none'; }}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
              Nouvel Écrin
            </button>
          ) : (
            <div style={{ padding: '8px 10px', display: 'flex', gap: 6 }} onClick={e => e.stopPropagation()}>
              <input data-testid={`picker-create-input-${photo.id}`} autoFocus value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleCreate(e); if (e.key === 'Escape') { setCreating(false); setNewName(''); } }}
                placeholder="Nom de l'écrin"
                style={{ flex: 1, fontFamily: "'Georgia', serif", fontSize: 12, padding: '8px 10px', borderRadius: 6, background: '#000', color: '#FAF3E0', border: '1px solid rgba(212,175,55,0.4)', outline: 'none' }} />
              <button data-testid={`picker-create-confirm-${photo.id}`} onClick={handleCreate} disabled={!newName.trim()}
                style={{ fontFamily: "'Georgia', serif", fontSize: 11, padding: '8px 14px', borderRadius: 6,
                         cursor: newName.trim() ? 'pointer' : 'not-allowed',
                         background: 'linear-gradient(145deg, #D4AF37, #B8860B)', color: '#000', border: 'none', fontWeight: 600,
                         opacity: newName.trim() ? 1 : 0.5 }}>
                Créer
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

const PhotoGrid = ({
  photos, viewMode, activeAlbumId, albumPhotos,
  selectMode, selectedIds, toggleSelect, openViewer,
  pendingComments, familyCircle, familyRole, setFamilyCircle,
  albums, setAlbums, token, t,
  toggleBook, setDeleteConfirm, deleteConfirm, handleDelete,
  albumPickerPhotoId, setAlbumPickerPhotoId,
  loadingMore, hasMore, loadMorePhotos,
  // Batch operations
  selectAll, deselectAll, handleBatchDelete, handleBatchBook,
  showBatchDeleteModal, setShowBatchDeleteModal,
  showBatchDateModal, setShowBatchDateModal,
  batchDate, setBatchDate, handleBatchDate, batchLoading,
  selectedForAlbum, setSelectedForAlbum,
  sounds,
}) => {
  const displayPhotos = activeAlbumId && albumPhotos ? albumPhotos : photos;
  const [downloadConfirm, setDownloadConfirm] = useState(null);
  const [duplicateEcrin, setDuplicateEcrin] = useState(null);
  const [linkPhotoId, setLinkPhotoId] = useState(null);
  const [faceGroups, setFaceGroups] = useState([]);
  const [newPersonName, setNewPersonName] = useState('');

  // Load face groups for linking modal
  useEffect(() => {
    if (!linkPhotoId || !token) return;
    fetch(`${API}/faces/groups`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => setFaceGroups((d.groups || []).filter(g => g.name)))
      .catch(() => {});
  }, [linkPhotoId, token]);

  const handleLinkToGroup = async (groupId) => {
    try {
      await fetch(`${API}/faces/manual-link`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ photo_id: linkPhotoId, group_id: groupId }),
      });
      toast.success('Photo identifiée');
      setLinkPhotoId(null);
    } catch { toast.error('Erreur'); }
  };

  const handleCreatePerson = async () => {
    if (!newPersonName.trim()) return;
    try {
      await fetch(`${API}/faces/manual-link`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ photo_id: linkPhotoId, person_name: newPersonName.trim() }),
      });
      toast.success(`Identifié : ${newPersonName.trim()}`);
      setLinkPhotoId(null);
      setNewPersonName('');
    } catch { toast.error('Erreur'); }
  };

  if (viewMode !== 'grid' || activeAlbumId) return null;

  return (
    <>
      {/* ═══ GRILLE DE PHOTOS ═══ */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-4" data-testid="tresors-grid">
        {displayPhotos.map((photo, idx) => (
          <PhotoCard
            key={photo.id}
            photo={photo} idx={idx}
            selectMode={selectMode} selectedIds={selectedIds} toggleSelect={toggleSelect}
            openViewer={openViewer} toggleBook={toggleBook}
            setDeleteConfirm={setDeleteConfirm}
            setDownloadConfirm={setDownloadConfirm}
            pendingComments={pendingComments}
            familyCircle={familyCircle} familyRole={familyRole} setFamilyCircle={setFamilyCircle}
            albums={albums} setAlbums={setAlbums}
            albumPickerPhotoId={albumPickerPhotoId} setAlbumPickerPhotoId={setAlbumPickerPhotoId}
            setDuplicateEcrin={setDuplicateEcrin}
            setLinkPhotoId={setLinkPhotoId}
            token={token} t={t}
          />
        ))}
      </div>

      {/* Load more */}
      {hasMore && (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '24px 0' }}>
          <button
            data-testid="load-more-btn"
            onClick={loadMorePhotos}
            disabled={loadingMore}
            onMouseEnter={(e) => { if (!loadingMore) { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 6px 20px rgba(212,175,55,0.45), inset 0 1px 0 rgba(255,255,255,0.4), inset 0 -2px 0 rgba(0,0,0,0.25)'; } }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 14px rgba(212,175,55,0.3), inset 0 1px 0 rgba(255,255,255,0.35), inset 0 -2px 0 rgba(0,0,0,0.2)'; }}
            style={{
              fontFamily: "'Georgia', serif", fontSize: 13, fontWeight: 600, letterSpacing: 0.4,
              padding: '11px 32px', borderRadius: 999, cursor: loadingMore ? 'default' : 'pointer',
              border: '1px solid rgba(184,134,11,0.85)',
              background: 'linear-gradient(145deg, #D4AF37, #B8860B)',
              color: '#1a1a1a', transition: 'all 0.25s ease',
              boxShadow: '0 4px 14px rgba(212,175,55,0.3), inset 0 1px 0 rgba(255,255,255,0.35), inset 0 -2px 0 rgba(0,0,0,0.2)',
              opacity: loadingMore ? 0.55 : 1,
            }}
          >
            {loadingMore ? 'Chargement...' : 'Voir plus de souvenirs'}
          </button>
        </div>
      )}

      {/* ═══ Confirmation de suppression ═══ */}
      {deleteConfirm && (
        <div data-testid="delete-confirm-modal" onClick={() => setDeleteConfirm(null)}
          style={{ position: 'fixed', inset: 0, zIndex: 9999, backgroundColor: 'rgba(0,0,0,0.88)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div onClick={e => e.stopPropagation()} style={{ backgroundColor: '#0a0a0a', borderRadius: 14, border: '1px solid rgba(168,7,26,0.45)', padding: '32px 28px', maxWidth: 380, width: '100%', textAlign: 'center', fontFamily: "'Georgia', 'Times New Roman', serif" }}>
            <div style={{ width: 56, height: 56, borderRadius: '50%', margin: '0 auto 18px', backgroundColor: 'rgba(168,7,26,0.10)', border: '2px solid rgba(168,7,26,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#A8071A" strokeWidth="2" strokeLinecap="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/></svg>
            </div>
            <p style={{ fontFamily: "'Georgia', serif", fontSize: 17, color: '#A8071A', marginBottom: 10, fontWeight: 500 }}>
              Voulez-vous vraiment supprimer ce trésor ?
            </p>
            <p style={{ fontFamily: "'Georgia', serif", fontSize: 13, color: '#FAF3E0', marginBottom: 26, lineHeight: 1.6 }}>
              Ce souvenir disparaîtra définitivement de votre Coffre-fort. Cette action est irréversible.
            </p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
              <button onClick={() => setDeleteConfirm(null)} data-testid="delete-cancel"
                style={{ fontFamily: "'Georgia', serif", fontSize: 13, padding: '10px 24px', borderRadius: 8, cursor: 'pointer', color: '#FAF3E0', backgroundColor: 'transparent', border: '1px solid rgba(212,175,55,0.4)', transition: 'all 0.3s' }}>
                {t('cancel')}
              </button>
              <button onClick={() => handleDelete(deleteConfirm)} data-testid="delete-confirm"
                style={{ fontFamily: "'Georgia', serif", fontSize: 13, padding: '10px 24px', borderRadius: 8, cursor: 'pointer', color: '#FAF3E0', background: 'linear-gradient(145deg, #A8071A, #6B0410)', border: '1px solid #A8071A', fontWeight: 500, transition: 'all 0.3s' }}>
                {t('dash_delete')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ Confirmation de téléchargement ═══ */}
      {downloadConfirm && (
        <div data-testid="download-confirm-modal" onClick={() => setDownloadConfirm(null)}
          style={{ position: 'fixed', inset: 0, zIndex: 60, backgroundColor: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div onClick={e => e.stopPropagation()} style={{ backgroundColor: '#111', borderRadius: 14, border: '1px solid rgba(212,175,55,0.3)', padding: '32px 28px', maxWidth: 360, width: '90%', textAlign: 'center' }}>
            <p style={{ fontFamily: "'Georgia', serif", fontSize: 16, color: '#D4AF37', marginBottom: 8 }}>
              Sauvegarder ce Tr{'\u00e9'}sor ?
            </p>
            <p style={{ fontFamily: "'Georgia', serif", fontSize: 13, color: '#FAF3E0', marginBottom: 24, lineHeight: 1.6 }}>
              Le souvenir sera t{'\u00e9'}l{'\u00e9'}charg{'\u00e9'} sur votre appareil.
            </p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
              <button onClick={() => setDownloadConfirm(null)} data-testid="download-cancel"
                style={{ fontFamily: "'Georgia', serif", fontSize: 13, padding: '10px 24px', borderRadius: 6, cursor: 'pointer', color: '#D4AF37', backgroundColor: 'transparent', border: '1px solid rgba(212,175,55,0.4)', transition: 'all 0.3s' }}>
                Annuler
              </button>
              <button onClick={() => { const a = document.createElement('a'); a.href = `${API}/photos/${downloadConfirm}/image?token=${token}`; a.download = 'tresor'; a.click(); setDownloadConfirm(null); }} data-testid="download-confirm-btn"
                style={{ fontFamily: "'Georgia', serif", fontSize: 13, padding: '10px 24px', borderRadius: 6, cursor: 'pointer', color: '#000', backgroundColor: 'rgba(212,175,55,0.08)', border: 'none', transition: 'all 0.3s' }}>
                T{'\u00e9'}l{'\u00e9'}charger
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ BARRE D'ACTIONS MULTI-SELECTION ═══ */}
      {selectMode && (
        <BatchActionBar
          selectedIds={selectedIds} photos={photos}
          selectAll={selectAll} deselectAll={deselectAll}
          setShowBatchDateModal={setShowBatchDateModal}
          setShowBatchDeleteModal={setShowBatchDeleteModal}
          handleBatchBook={handleBatchBook}
          albums={albums} setAlbums={setAlbums}
          selectedForAlbum={selectedForAlbum} setSelectedForAlbum={setSelectedForAlbum}
          familyCircle={familyCircle} familyRole={familyRole} setFamilyCircle={setFamilyCircle}
          token={token} t={t}
        />
      )}

      {/* ═══ MODALE SUPPRESSION GROUPEE ═══ */}
      {showBatchDeleteModal && (
        <BatchDeleteModal
          selectedIds={selectedIds} batchLoading={batchLoading}
          handleBatchDelete={handleBatchDelete}
          onClose={() => setShowBatchDeleteModal(false)}
          t={t}
        />
      )}

      {/* ═══ MODALE DATE GROUPEE ═══ */}
      {showBatchDateModal && (
        <BatchDateModal
          selectedIds={selectedIds} batchDate={batchDate} setBatchDate={setBatchDate}
          batchLoading={batchLoading} handleBatchDate={handleBatchDate}
          onClose={() => { setShowBatchDateModal(false); setBatchDate(''); }}
          t={t}
        />
      )}

      {/* ═══ MODALE DOUBLON ÉCRIN (style Bobine) ═══ */}

      {/* ═══ MODALE RECONNAISSANCE (lier à une personne) ═══ */}
      {linkPhotoId && (
        <div data-testid="face-link-modal" onClick={() => { setLinkPhotoId(null); setNewPersonName(''); }}
          style={{ position: 'fixed', inset: 0, zIndex: 9999, backgroundColor: 'rgba(0,0,0,0.88)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div onClick={e => e.stopPropagation()} style={{
            backgroundColor: '#0a0a0a', border: '1px solid #D4AF37', borderRadius: 12,
            padding: 24, maxWidth: 340, width: '90%',
          }}>
            <p style={{ fontFamily: "'Georgia', serif", fontSize: 16, color: '#D4AF37', marginBottom: 16, textAlign: 'center' }}>
              Reconna{'\u00ee'}tre ce Tr{'\u00e9'}sor
            </p>
            {faceGroups.length > 0 && (
              <>
                <p style={{ fontFamily: "'Georgia', serif", fontSize: 11, color: '#FAF3E0', marginBottom: 8 }}>Personnes connues :</p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
                  {faceGroups.map(g => (
                    <button key={g.group_id} data-testid={`link-group-${g.group_id}`}
                      onClick={() => handleLinkToGroup(g.group_id)}
                      style={{
                        fontFamily: "'Georgia', serif", fontSize: 12, padding: '6px 14px', borderRadius: 20,
                        cursor: 'pointer', color: '#000', background: 'linear-gradient(145deg, #D4AF37, #B8860B)', border: 'none', fontWeight: 600,
                      }}>
                      {g.name}
                    </button>
                  ))}
                </div>
              </>
            )}
            <p style={{ fontFamily: "'Georgia', serif", fontSize: 11, color: '#FAF3E0', marginBottom: 8 }}>Nouvelle personne :</p>
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                data-testid="new-person-input"
                value={newPersonName}
                onChange={e => setNewPersonName(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleCreatePerson(); }}
                placeholder="Pr{'\u00e9'}nom"
                style={{
                  flex: 1, padding: '8px 12px', borderRadius: 8,
                  border: '1px solid rgba(212,175,55,0.3)', backgroundColor: 'rgba(212,175,55,0.04)',
                  fontFamily: "'Georgia', serif", fontSize: 13, color: '#FAF3E0', outline: 'none',
                }}
                autoFocus
              />
              <button
                data-testid="create-person-btn"
                onClick={handleCreatePerson}
                style={{
                  padding: '8px 16px', borderRadius: 8, cursor: 'pointer',
                  background: 'linear-gradient(145deg, #D4AF37, #B8860B)', border: 'none',
                  fontFamily: "'Georgia', serif", fontSize: 12, fontWeight: 600, color: '#000',
                }}>
                OK
              </button>
            </div>
            <button onClick={() => { setLinkPhotoId(null); setNewPersonName(''); }}
              style={{ marginTop: 14, width: '100%', padding: '8px 0', background: 'none', border: 'none', cursor: 'pointer', fontFamily: "'Georgia', serif", fontSize: 12, color: '#FAF3E0' }}>
              Annuler
            </button>
          </div>
        </div>
      )}

      {duplicateEcrin && (
        <div onClick={() => setDuplicateEcrin(null)} style={{ position: 'fixed', inset: 0, zIndex: 9999, backgroundColor: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
          <div onClick={e => e.stopPropagation()} style={{
            backgroundColor: '#0a0a0a', border: '1px solid #D4AF37', borderRadius: 12,
            padding: '28px 32px', maxWidth: 300, textAlign: 'center',
            boxShadow: '0 12px 40px rgba(0,0,0,0.8)',
          }}>
            <div style={{ fontFamily: "'Georgia', serif", fontSize: 16, color: '#D4AF37', marginBottom: 10 }}>
              Déjà en sécurité
            </div>
            <div style={{ fontFamily: "'Georgia', serif", fontSize: 13, color: '#D4AF37', fontStyle: 'italic', lineHeight: 1.5 }}>
              Ce trésor est déjà rangé dans l'écrin <strong style={{ color: '#D4AF37' }}>{duplicateEcrin.name}</strong>.
            </div>
            <button data-testid="duplicate-ecrin-close" onClick={() => setDuplicateEcrin(null)} style={{
              marginTop: 20, fontFamily: "'Georgia', serif", fontSize: 12, color: '#000',
              backgroundColor: 'rgba(212,175,55,0.08)', border: 'none', borderRadius: 6,
              padding: '8px 24px', cursor: 'pointer', letterSpacing: 0.3,
            }}>
              Compris
            </button>
          </div>
        </div>
      )}

      {/* ═══ Écrin tooltips (fixed position, outside overflow:hidden cards) ═══ */}
      {photos.map(photo => {
        const ecrinNames = albums.filter(a => (a.photo_ids || []).includes(photo.id)).map(a => a.name);
        if (!ecrinNames.length) return null;
        return (
          <div key={`ecrin-tip-${photo.id}`} id={`ecrin-tip-${photo.id}`} style={{
            display: 'none', position: 'fixed', zIndex: 9999,
            whiteSpace: 'nowrap', pointerEvents: 'none',
            backgroundColor: '#0a0a0a', color: '#D4AF37',
            border: '1px solid #D4AF37', borderRadius: 6,
            padding: '4px 10px',
            fontFamily: "'Georgia', serif", fontSize: 10, fontStyle: 'italic',
            boxShadow: '0 4px 12px rgba(0,0,0,0.6)',
          }}>
            En sécurité dans {ecrinNames.join(', ')}
          </div>
        );
      })}
    </>
  );
};

/* ── Photo Card ── */
const PhotoCard = ({
  photo, idx, selectMode, selectedIds, toggleSelect, openViewer, toggleBook,
  setDeleteConfirm, setDownloadConfirm, pendingComments, familyCircle, familyRole, setFamilyCircle,
  albums, setAlbums, albumPickerPhotoId, setAlbumPickerPhotoId, setDuplicateEcrin, setLinkPhotoId, token, t,
}) => {
  const cardRef = { el: null };
  const cardUid = `${photo.id}-${idx}`;
  const [removedFaceNames, setRemovedFaceNames] = useState([]);

  const handleRemoveFaceName = async (name, e) => {
    e.stopPropagation();
    setRemovedFaceNames((prev) => [...prev, name]);
    try {
      await fetch(`${API}/faces/remove-name-from-photo`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ photo_id: photo.id, name }),
      });
      toast(`« ${name} » retiré de cette photo`, { style: { fontFamily: "'Georgia', serif", backgroundColor: '#0a0a0a', border: '1px solid rgba(212,175,55,0.3)', color: '#D4AF37' }, duration: 2200 });
    } catch {
      // restore on error
      setRemovedFaceNames((prev) => prev.filter((n) => n !== name));
      toast.error('Suppression impossible');
    }
  };

  const visibleFaceNames = (photo.face_names || []).filter((n) => !removedFaceNames.includes(n));

  return (
    <div
      data-testid={`tresor-${photo.id}`}
      className="tresor-card"
      onClick={(e) => {
        if (selectMode) { e.stopPropagation(); toggleSelect(photo.id); return; }
        // Open lightbox (visionneuse plein écran)
        openViewer(idx);
      }}
      style={{
        position: 'relative', borderRadius: 10,
        cursor: 'pointer',
        touchAction: 'pan-y', userSelect: 'none', WebkitUserSelect: 'none',
      }}
    >
      {/* Image wrapper — clips photo to rounded corners, provides card dimensions */}
      <div style={{
        borderRadius: 10, overflow: 'hidden',
        border: selectMode && selectedIds.has(photo.id) ? '2px solid #D4AF37' : photo.in_book ? '2px solid #D4AF37' : '1px solid rgba(212,175,55,0.15)',
        transition: 'border-color 0.3s',
        boxShadow: selectMode && selectedIds.has(photo.id) ? '0 0 16px rgba(212,175,55,0.3)' : 'none',
      }}>
      {/* Selection checkbox */}
      {selectMode && (
        <div data-testid={`select-check-${photo.id}`} style={{
          position: 'absolute', top: 8, left: 8, zIndex: 5, width: 26, height: 26, borderRadius: 6,
          backgroundColor: selectedIds.has(photo.id) ? '#D4AF37' : 'rgba(0,0,0,0.6)',
          border: selectedIds.has(photo.id) ? '2px solid #D4AF37' : '2px solid rgba(212,175,55,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s',
        }}>
          {selectedIds.has(photo.id) && (
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#000" strokeWidth="3" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>
          )}
        </div>
      )}

      {/* Thumbnail */}
      <div style={{
        width: '100%', aspectRatio: '4/3', position: 'relative',
        background: 'linear-gradient(135deg, rgba(212,175,55,0.04) 25%, rgba(212,175,55,0.08) 50%, rgba(212,175,55,0.04) 75%)',
        backgroundSize: '200% 200%', animation: 'shimmer 1.5s ease infinite',
      }}>
        <img
          src={`${API}/photos/${photo.id}/thumbnail?token=${token}`}
          alt="Souvenir" loading="lazy"
          draggable={false}
          onDragStart={e => e.preventDefault()}
          onLoad={e => { e.target.style.opacity = '1'; e.target.parentElement.style.animation = 'none'; }}
          onError={e => {
            const img = e.target;
            const retries = parseInt(img.dataset.retries || '0');
            if (retries < 3) {
              img.dataset.retries = String(retries + 1);
              setTimeout(() => { img.src = `${API}/photos/${photo.id}/thumbnail?token=${token}&r=${retries + 1}`; }, 2000 * (retries + 1));
            } else {
              img.style.opacity = '0.3'; img.style.objectFit = 'contain'; img.style.padding = '25%'; img.style.filter = 'drop-shadow(0 0 6px rgba(212,175,55,0.15))'; img.style.transition = 'opacity 0.6s ease'; img.parentElement.style.background = 'radial-gradient(ellipse at 30% 30%, #1a1714 0%, #0d0b09 50%, #080706 100%)'; img.parentElement.style.animation = 'none'; img.src = '/bobine-face-logo.png';
            }
          }}
          style={{ display: 'block', width: '100%', height: '100%', objectFit: 'cover', opacity: 0, transition: 'opacity 0.3s ease', transform: photo.rotation ? `rotate(${photo.rotation}deg)` : 'none' }}
        />
      </div>

      {/* Poinçon Livre — toujours visible, 2 états (in_book / out) */}
      {!selectMode && (
        <button
          data-testid={`book-indicator-${photo.id}`}
          onClick={(e) => {
            e.stopPropagation();
            const wasIn = photo.in_book;
            toggleBook(photo.id);
            toast.success(wasIn ? 'Retiré du Livre' : 'Ajouté au Livre');
          }}
          title={photo.in_book ? 'Retirer du Livre' : 'Ajouter au Livre'}
          style={{
            position: 'absolute', top: 6, left: 6, width: 26, height: 26, borderRadius: '50%',
            background: photo.in_book ? 'linear-gradient(145deg, #D4AF37, #B8860B)' : 'rgba(10,10,10,0.65)',
            border: '1.5px solid #D4AF37',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 8, cursor: 'pointer', padding: 0, transition: 'all 0.2s',
          }}
        >
          <svg width="12" height="12" viewBox="0 0 24 24"
               fill={photo.in_book ? '#000' : 'none'}
               stroke={photo.in_book ? '#000' : '#D4AF37'} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20V2H6.5A2.5 2.5 0 0 1 4 4.5v15z"/>
          </svg>
        </button>
      )}

      {/* Audio badge */}
      {photo.audio_filename && (
        <SceauIndicator type="audio" style={{ position: 'absolute', top: 8, left: photo.in_book ? 108 : 8 }} />
      )}

      {/* Comment badge */}
      {pendingComments.some(c => c.status === 'approved' && c.photo_id === photo.id) && (
        <SceauIndicator type="comment" style={{ position: 'absolute', top: photo.audio_filename ? 34 : 8, left: photo.in_book ? 108 : 8 }} />
      )}

      {/* Bouton Écrin — toujours visible en bas à droite, ouvre le menu déroulant */}
      {!selectMode && (() => {
        const isInEcrin = albums.some(a => (a.photo_ids || []).includes(photo.id));
        return (
          <button
            data-testid={`ecrin-trigger-${photo.id}`}
            onClick={(e) => { e.stopPropagation(); setAlbumPickerPhotoId(albumPickerPhotoId === cardUid ? null : cardUid); }}
            title={isInEcrin ? 'Gérer les écrins' : 'Ranger dans un écrin'}
            style={{
              position: 'absolute', bottom: 6, right: 6, width: 28, height: 28, borderRadius: '50%',
              background: isInEcrin ? 'linear-gradient(145deg, #D4AF37, #B8860B)' : 'rgba(10,10,10,0.7)',
              border: '1.5px solid #D4AF37',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              zIndex: 8, cursor: 'pointer', padding: 0, transition: 'all 0.2s',
              boxShadow: '0 2px 6px rgba(0,0,0,0.4)',
            }}
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
                 stroke={isInEcrin ? '#000' : '#D4AF37'} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z"/>
            </svg>
          </button>
        );
      })()}

      {/* Clean — no per-photo buttons */}
      </div>

      {/* Date */}
      <div style={{ position: 'absolute', top: 8, right: 8, fontFamily: "'Georgia', serif", fontSize: 10, color: '#D4AF37', backgroundColor: 'rgba(0,0,0,0.6)', padding: '2px 6px', borderRadius: 3 }}>
        {new Date(photo.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}
      </div>

      {/* Face names — chaque tag affiche une croix pour le retirer de cette photo */}
      {visibleFaceNames.length > 0 && (
        <div data-testid={`face-names-${photo.id}`} style={{
          position: 'absolute', bottom: 28, left: 0, right: 38,
          display: 'flex', flexWrap: 'wrap', gap: 4, justifyContent: 'center',
        }}>
          {visibleFaceNames.map((name) => (
            <span key={name} style={{
              fontFamily: "'Georgia', serif", fontSize: 9, letterSpacing: 0.5,
              color: '#D4AF37', backgroundColor: 'rgba(0,0,0,0.7)',
              padding: '2px 4px 2px 8px', borderRadius: 10,
              display: 'inline-flex', alignItems: 'center', gap: 4,
            }}>
              {name}
              <button
                data-testid={`remove-face-${photo.id}-${name}`}
                onClick={(e) => handleRemoveFaceName(name, e)}
                title={`Retirer « ${name} » de cette photo`}
                style={{ background: 'rgba(212,175,55,0.25)', border: 'none', borderRadius: '50%',
                         width: 14, height: 14, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                         color: '#D4AF37', fontSize: 10, lineHeight: 1, cursor: 'pointer', padding: 0 }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(212,175,55,0.55)'; e.currentTarget.style.color = '#000'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(212,175,55,0.25)'; e.currentTarget.style.color = '#D4AF37'; }}>
                ×
              </button>
            </span>
          ))}
        </div>
      )}
      {/* Bouton "+ Identifier" — sur les photos SANS tag de visage */}
      {!selectMode && visibleFaceNames.length === 0 && (
        <button
          data-testid={`identify-btn-${photo.id}`}
          onClick={(e) => { e.stopPropagation(); setLinkPhotoId(photo.id); }}
          title="Identifier le visage sur cette photo"
          style={{
            position: 'absolute', bottom: 6, left: 6, zIndex: 8,
            display: 'inline-flex', alignItems: 'center', gap: 4,
            fontFamily: "'Georgia', serif", fontSize: 9, letterSpacing: 0.3,
            color: '#D4AF37', background: 'rgba(10,10,10,0.7)',
            border: '1px solid rgba(212,175,55,0.4)',
            padding: '3px 8px', borderRadius: 10, cursor: 'pointer',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(212,175,55,0.15)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(10,10,10,0.7)'; }}
        >
          <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          Identifier
        </button>
      )}

      {/* Album picker dropdown — opens when "Écrin" button or badge is clicked */}
      <AlbumPickerDropdown
        photo={photo} cardUid={cardUid} albums={albums} setAlbums={setAlbums}
        albumPickerPhotoId={albumPickerPhotoId} setAlbumPickerPhotoId={setAlbumPickerPhotoId}
        setDuplicateEcrin={setDuplicateEcrin} token={token}
      />
    </div>
  );
};

/* ── Batch Action Bar — ALL functions ── */
const BatchActionBar = ({ selectedIds, photos, selectAll, deselectAll, setShowBatchDateModal, setShowBatchDeleteModal, handleBatchBook, albums, setAlbums, selectedForAlbum, setSelectedForAlbum, familyCircle, familyRole, setFamilyCircle, setDownloadConfirm, token, t }) => {
  const [showEcrinPicker, setShowEcrinPicker] = React.useState(false);
  const API = `${(process.env.REACT_APP_BACKEND_URL || 'https://bobine.live')}/api`;
  const btnStyle = { background: 'none', border: '1px solid rgba(212,175,55,0.2)', borderRadius: 8, padding: '8px 12px', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, color: '#D4AF37', fontFamily: "'Georgia', serif", fontSize: 9, minWidth: 52 };

  return (
    <div data-testid="batch-action-bar" style={{
      position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 55,
      backgroundColor: 'rgba(10,10,10,0.97)', borderTop: '1px solid #D4AF37',
      padding: '10px 12px 28px', animation: 'fadeInUp 0.3s ease-out',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <span style={{ fontFamily: "'Georgia', serif", fontSize: 13, color: '#D4AF37', fontWeight: 600 }}>
          {selectedIds.size} s{'\u00e9'}lectionn{'\u00e9'}{selectedIds.size > 1 ? 's' : ''}
        </span>
        <button onClick={selectedIds.size === photos.length ? deselectAll : selectAll}
          style={{ fontFamily: "'Georgia', serif", fontSize: 11, color: '#D4AF37', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>
          {selectedIds.size === photos.length ? 'Tout d\u00e9s\u00e9lectionner' : 'Tout s\u00e9lectionner'}
        </button>
      </div>
      <div style={{ display: 'flex', justifyContent: 'center', gap: 8, flexWrap: 'wrap' }}>
        <button data-testid="batch-memory-btn" onClick={() => { if (selectedIds.size > 0) handleBatchBook(true); }} style={btnStyle}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#D4AF37" strokeWidth="1.5" strokeLinecap="round"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/></svg>
          M{'\u00e9'}moire
        </button>
        <button data-testid="batch-save-btn" onClick={() => {
          if (selectedIds.size > 0) {
            selectedIds.forEach(id => {
              const link = document.createElement('a');
              link.href = `${API}/photos/${id}/image?token=${token}`;
              link.download = `tresor-${id}.jpg`;
              link.click();
            });
          }
        }} style={btnStyle}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#D4AF37" strokeWidth="1.5" strokeLinecap="round"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
          Sauver
        </button>
        {familyCircle && familyRole === 'guardian' && (
          <button data-testid="batch-circle-btn" onClick={async () => {
            if (selectedIds.size === 0) return;
            try {
              await fetch(`${API}/family/share-photos`, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify({ photo_ids: Array.from(selectedIds) }) });
              setFamilyCircle(prev => ({ ...prev, shared_photo_ids: [...new Set([...(prev.shared_photo_ids || []), ...selectedIds])] }));
              toast(`${selectedIds.size} photo(s) partag\u00e9e(s)`, { style: { fontFamily: "'Georgia', serif", backgroundColor: '#0a0a0a', border: '1px solid rgba(212,175,55,0.3)', color: '#D4AF37' } });
            } catch {}
          }} style={btnStyle}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#D4AF37" strokeWidth="1.5" strokeLinecap="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/></svg>
            Cercle
          </button>
        )}
        <div style={{ position: 'relative' }}>
          <button data-testid="batch-ecrin-btn" onClick={() => setShowEcrinPicker(!showEcrinPicker)} style={btnStyle}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#D4AF37" strokeWidth="1.5" strokeLinecap="round"><path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z"/></svg>
            {'\u00c9'}crin
          </button>
          {showEcrinPicker && albums.length > 0 && (
            <div style={{ position: 'absolute', bottom: '100%', left: '50%', transform: 'translateX(-50%)', marginBottom: 8, backgroundColor: '#0a0a0a', border: '1px solid #D4AF37', borderRadius: 8, padding: 6, minWidth: 160, zIndex: 60 }}>
              {albums.map(a => (
                <button key={a.id} onClick={async () => {
                  const count = selectedIds.size;
                  try { await fetch(`${API}/albums/${a.id}/add-photos`, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify({ photo_ids: Array.from(selectedIds) }) }); if (count >= 3) playGoldSound(); toast(`Ajout\u00e9 \u00e0 ${a.name}`, { style: { fontFamily: "'Georgia', serif", backgroundColor: '#0a0a0a', border: '1px solid rgba(212,175,55,0.3)', color: '#D4AF37' } }); } catch {}
                  setShowEcrinPicker(false);
                }} style={{ width: '100%', textAlign: 'left', fontFamily: "'Georgia', serif", fontSize: 11, color: '#D4AF37', background: 'none', border: 'none', padding: '8px 10px', cursor: 'pointer' }}>
                  {a.name}
                </button>
              ))}
            </div>
          )}
        </div>
        <button data-testid="batch-date-btn" onClick={() => { if (selectedIds.size > 0) setShowBatchDateModal(true); }} style={btnStyle}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#D4AF37" strokeWidth="1.5"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
          Date
        </button>
        <button data-testid="batch-delete-btn" onClick={() => { if (selectedIds.size > 0) setShowBatchDeleteModal(true); }} style={{ ...btnStyle, borderColor: 'rgba(192,57,43,0.3)', color: '#c0392b' }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#c0392b" strokeWidth="1.5"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/></svg>
          Supprimer
        </button>
      </div>
    </div>
  );
};

/* ── Batch Delete Modal ── */
const BatchDeleteModal = ({ selectedIds, batchLoading, handleBatchDelete, onClose, t }) => (
  <div data-testid="batch-delete-modal" onClick={onClose}
    style={{ position: 'fixed', inset: 0, zIndex: 9999, backgroundColor: 'rgba(0,0,0,0.88)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
    <div onClick={e => e.stopPropagation()} style={{ backgroundColor: '#0a0a0a', borderRadius: 14, border: '1px solid rgba(168,7,26,0.45)', padding: '36px 32px', maxWidth: 420, width: '100%', textAlign: 'center', fontFamily: "'Georgia', 'Times New Roman', serif" }}>
      <div style={{ width: 56, height: 56, borderRadius: '50%', margin: '0 auto 20px', backgroundColor: 'rgba(168,7,26,0.10)', border: '2px solid rgba(168,7,26,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#A8071A" strokeWidth="2" strokeLinecap="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/></svg>
      </div>
      <p style={{ fontFamily: "'Georgia', serif", fontSize: 18, color: '#A8071A', marginBottom: 10, fontWeight: 500 }}>
        Supprimer {selectedIds.size} {selectedIds.size > 1 ? t('svc_treasures').toLowerCase() : t('photo')} ?
      </p>
      <p style={{ fontFamily: "'Georgia', serif", fontSize: 13, color: '#FAF3E0', marginBottom: 28, lineHeight: 1.6 }}>
        {selectedIds.size > 1 ? `Ces ${selectedIds.size} souvenirs disparaîtront` : 'Ce souvenir disparaîtra'} définitivement de votre Coffre-fort. Cette action est irréversible.
      </p>
      <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
        <button data-testid="batch-delete-cancel" onClick={onClose}
          style={{ fontFamily: "'Georgia', serif", fontSize: 13, padding: '10px 24px', borderRadius: 8, cursor: 'pointer', color: '#FAF3E0', backgroundColor: 'transparent', border: '1px solid rgba(212,175,55,0.4)' }}>{t('cancel')}</button>
        <button data-testid="batch-delete-confirm" onClick={handleBatchDelete} disabled={batchLoading}
          style={{ fontFamily: "'Georgia', serif", fontSize: 13, padding: '10px 24px', borderRadius: 8, cursor: batchLoading ? 'wait' : 'pointer', color: '#FAF3E0', background: 'linear-gradient(145deg, #A8071A, #6B0410)', border: '1px solid #A8071A', fontWeight: 500, opacity: batchLoading ? 0.6 : 1 }}>
          {batchLoading ? t('dash_batch_deleting') : `${t('dash_delete')} (${selectedIds.size})`}
        </button>
      </div>
    </div>
  </div>
);

/* ── Batch Date Modal ── */
const BatchDateModal = ({ selectedIds, batchDate, setBatchDate, batchLoading, handleBatchDate, onClose, t }) => (
  <div data-testid="batch-date-modal" onClick={onClose}
    style={{ position: 'fixed', inset: 0, zIndex: 9999, backgroundColor: 'rgba(0,0,0,0.88)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
    <div onClick={e => e.stopPropagation()} style={{ backgroundColor: '#111', borderRadius: 14, border: '1px solid rgba(212,175,55,0.3)', padding: '36px 32px', maxWidth: 420, width: '90%', textAlign: 'center' }}>
      <div style={{ width: 56, height: 56, borderRadius: '50%', margin: '0 auto 20px', backgroundColor: 'rgba(212,175,55,0.08)', border: '2px solid rgba(212,175,55,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#D4AF37" strokeWidth="1.5"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
      </div>
      <p style={{ fontFamily: "'Georgia', serif", fontSize: 18, color: '#D4AF37', marginBottom: 8 }}>
        {t('dash_batch_modify_date')} — {selectedIds.size}
      </p>
      <p style={{ fontFamily: "'Georgia', serif", fontSize: 13, color: '#FAF3E0', marginBottom: 24, lineHeight: 1.6 }}>
        {selectedIds.size > 1 ? t('dash_batch_same_date_plural') : t('dash_batch_same_date_single')}
      </p>
      <input data-testid="batch-date-input" type="date" value={batchDate} onChange={e => setBatchDate(e.target.value)}
        style={{ fontFamily: "'Georgia', serif", fontSize: 14, color: '#D4AF37', backgroundColor: 'rgba(212,175,55,0.05)', border: '1px solid rgba(212,175,55,0.3)', borderRadius: 8, padding: '10px 16px', width: '100%', outline: 'none', colorScheme: 'dark', marginBottom: 24 }} />
      <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
        <button data-testid="batch-date-cancel" onClick={onClose}
          style={{ fontFamily: "'Georgia', serif", fontSize: 13, padding: '10px 24px', borderRadius: 6, cursor: 'pointer', color: '#D4AF37', backgroundColor: 'transparent', border: '1px solid rgba(212,175,55,0.4)' }}>{t('cancel')}</button>
        <button data-testid="batch-date-apply" onClick={handleBatchDate} disabled={!batchDate || batchLoading}
          style={{ fontFamily: "'Georgia', serif", fontSize: 13, padding: '10px 24px', borderRadius: 6, cursor: (!batchDate || batchLoading) ? 'not-allowed' : 'pointer', color: '#000', background: 'linear-gradient(145deg, #D4AF37, #B8860B)', border: 'none', opacity: (!batchDate || batchLoading) ? 0.5 : 1 }}>
          {batchLoading ? t('dash_applying') : t('dash_apply')}
        </button>
      </div>
    </div>
  </div>
);

export default PhotoGrid;
